/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useMemo, type FC } from 'react'
import { Button, Col, Form, InputGroup, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import TextEditor from '../TextEditor'

const validationSchema = yup.object().shape({
  title: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  slug: yup.string().required('O slug é obrigatório').default('').min(0).max(255),
  content: yup.string().nullable().default(''),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('active')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormPageHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormPageHandleSubmit,
  initialValues?: ValidationSchema,
  isLoading?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormPage is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormPage from './src/views/PostEdit/FormPage.tsx'
 *
 * const MyComponent = () => (
 *   <FormPage />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormPage: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Título</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o título da página'
            name='title'
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Visibilidade</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Slug</Form.Label>
          <InputGroup className="mb-3">
            <InputGroup.Text id="basic-addon1">
              https://www.supermercados-mundial.com.br/
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="minha-pagina"
              name='slug'
              value={formik.values.slug}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
          <Form.Text className="text-muted">
            O slug é utilizado para identificar a página na URL do site,
            {' '}
            por exemplo: https://www.supermercados-mundial.com.br/pagina-personalizada
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Conteúdo</Form.Label>
          <TextEditor
            value={formik?.values?.content ?? ''}
            placeholder='Escreva aqui o conteúdo da página'
            readOnly={formik.isSubmitting || isLoading}
            onChange={value => formik.setFieldValue('content', value)}
          />
          {/* <Form.Text className="text-muted">
                  Escreva aqui o conteúdo da página
                </Form.Text> */}
        </Form.Group>
        <Col md={12}>
          <Stack direction="horizontal" gap={3}>
            <div className="p-2 ms-auto">
              <Button
                size='sm'
                variant="outline-link"
                style={{ marginRight: 10 }} onClick={() => navigate('/pages')}
                disabled={formik.isSubmitting || isLoading}
              >
                Voltar
              </Button>
            </div>
            <div className="p-2">
              <Button
                size='sm'
                variant="outline-primary"
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
              >
                Salvar
              </Button>
            </div>
            <div className="p-2">
              {customFooterMessage}
            </div>
          </Stack>
        </Col>
      </Row>
    </Form>
  )
}

export default FormPage
